import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { NS } from '../../../models/I18n';
import { EligibilitySubmission, Program, SimpleApplication, Type, User } from '../../../models/UserServerData';
import ProgramTypesFilter from '../../../shared/components/ProgramTypesFilter';
import CrsSection, { SuperVisasSideBar } from '../components/CrsSection';
import { Organization } from '../../../models/PartnerServerData';
import { SimpleUserWithFullName } from '../../../models/PartnerWebsiteServerData';
import { LeadActions } from '../components/eligibility/LeadActions';
import * as apiClient from '../../apiClient';
import { getItemFromLocalStorage } from '../../../shared/utils/storage-api';
import Spinner from '../../../shared/components/loading/Spinner';
import { VisitorVisaExemptView } from '../../../shared/components/VisitorVisaExemptView';
import { useProgramTypesQuery, useUserInfoQuery } from '../../SubmissionAPI';
import { DynamicProgramCard } from '../components/eligibility/ProgramCard';

interface SuperVisaIneligibleViewProps {
  submission: EligibilitySubmission;
}

function SuperVisaIneligibleView({ submission }: SuperVisaIneligibleViewProps) {
  const { program_type: programType, country } = submission;
  const { t } = useTranslation(NS.Shared);
  const getText = (type: Type) => {
    if (type === 'study') {
      return (
        <div className="mb-24 w-full rounded border-2 border-solid border-gray-200 p-3 font-medium text-gray-700">
          <Trans
            i18nKey={
              country === 'CA' ? 'eligibilityResult.study-ineligible-ca' : 'eligibilityResult.study-ineligible-us'
            }
            t={t}
          >
            Based on your answers, you are currently ineligible for any <span className="text-brand-blue">Study</span>{' '}
            programs as you do not have enough funds. Before you can be approved for a study permit, you will need to
            show at least $25,000 in proof of fund to the Canadian government that you can pay for tuition and living
            cost. If you believe you can provide the proof of fund, try our eligibility check again!
          </Trans>
        </div>
      );
    }
    if (type === 'work') {
      return (
        <div className="mb-24 w-full rounded border-2 border-solid border-gray-200 p-3 font-medium text-gray-700">
          <Trans
            i18nKey={country === 'CA' ? 'eligibilityResult.work-ineligible-ca' : 'eligibilityResult.work-ineligible-us'}
            t={t}
          >
            Based on your answers, you are currently ineligible for any <span className="text-brand-blue">Work</span>{' '}
            programs. To improve your eligibility, you can try to find an employer that can sponsor your work permit.
            Here are some platforms to find employers that are hiring:
          </Trans>
          <ul className="ml-4 list-disc">
            {country === 'CA' ? (
              <>
                <li>
                  <div>Job Bank: </div>
                  <a href="https://www.jobbank.gc.ca/home" target="_blank" rel="noreferrer">
                    https://www.jobbank.gc.ca/home
                  </a>
                </li>
                <li>
                  <div>Indeed: </div>
                  <a href="https://ca.indeed.com/" target="_blank" rel="noreferrer">
                    https://ca.indeed.com
                  </a>
                </li>
                <li>
                  <div>Monster: </div>
                  <a href="https://www.monster.ca" target="_blank" rel="noreferrer">
                    https://www.monster.ca
                  </a>
                </li>
                <li>
                  <div>Linkedin: </div>
                  <a href="https://www.linkedin.com/jobs/?originalSubdomain=ca" target="_blank" rel="noreferrer">
                    https://www.linkedin.com/jobs/?originalSubdomain=ca
                  </a>
                </li>
                <li>
                  <div>Glassdoor: </div>
                  <a href="https://www.glassdoor.ca" target="_blank" rel="noreferrer">
                    https://www.glassdoor.ca
                  </a>
                </li>
                <li>
                  <div>Workopolis: </div>
                  <a href="https://www.workopolis.com/en" target="_blank" rel="noreferrer">
                    https://www.workopolis.com/en
                  </a>
                </li>
              </>
            ) : (
              <>
                <li>
                  <div>Indeed: </div>
                  <a href="https://www.indeed.com" target="_blank" rel="noreferrer">
                    https://www.indeed.com
                  </a>
                </li>
                <li>
                  <div>Monster: </div>
                  <a href="https://www.monster.com" target="_blank" rel="noreferrer">
                    https://www.monster.com
                  </a>
                </li>
                <li>
                  <div>Linkedin: </div>
                  <a href="https://www.linkedin.com" target="_blank" rel="noreferrer">
                    https://www.linkedin.com
                  </a>
                </li>
                <li>
                  <div>Glassdoor: </div>
                  <a href="https://www.glassdoor.com" target="_blank" rel="noreferrer">
                    https://www.glassdoor.com
                  </a>
                </li>
              </>
            )}
          </ul>
        </div>
      );
    }
    if (type === 'visitor') {
      return (
        <div className="mb-24 w-full rounded border-2 border-solid border-gray-200 p-3 font-medium text-gray-700">
          <Trans
            i18nKey={
              country === 'CA' ? 'eligibilityResult.visitor-ineligible-ca' : 'eligibilityResult.visitor-ineligible-us'
            }
            t={t}
          >
            Based on your answers, you are currently ineligible for any <span className="text-brand-blue">Visit</span>{' '}
            programs as you do not have enough funds. Before you can be approved for a visit permit, you will need to
            show at least $10,000 in proof of fund to the Canadian government that you can pay for your travel. If you
            believe you can provide the proof of fund, try our eligibility check again!
          </Trans>
        </div>
      );
    }
    if (type === 'immigrate') {
      return (
        <div className="mb-24 w-full rounded border-2 border-solid border-gray-200 p-3 font-medium text-gray-700">
          <Trans i18nKey="eligibilityResult.immigrate-ineligible" t={t}>
            Based on your answers, we did not find any <span className="text-brand-blue">immigrate</span> programs that
            you can apply for. You may be eligible for a work, study or visit visa instead. Please try our eligibility
            check again.
          </Trans>
          <br />
          <Trans i18nKey="eligibilityResult.more-info" t={t}>
            Find more information about the immigration options in Canada and the U.S. here:{' '}
            <a className="text-brand-blue hover:underline" href="https://supervisas.com/visas">
              https://supervisas.com/visas
            </a>
          </Trans>
        </div>
      );
    }
    return (
      <div className="mb-24 w-full rounded border-2 border-solid border-gray-200 p-3 font-medium text-gray-700">
        <Trans i18nKey="eligibilityResult.unknown-program-type" t={t}>
          Unknown program type: {{ programType }}
        </Trans>
      </div>
    );
  };
  return (
    <div className="min-h-screen w-full bg-white pt-3 lg:w-3/4 lg:pr-24">
      <div className="px-8">
        <div className="mb-6 text-lg font-semibold text-gray-600 lg:text-lg">{t('program.eligible-programs')}</div>
        {getText(programType)}
      </div>
      <LeadActions isEligible={false} submission={submission} />
    </div>
  );
}

interface IneligibleProgramsViewProps {
  organization?: Organization;
  submission: EligibilitySubmission;
}

function IneligibleProgramsView({ organization, submission }: IneligibleProgramsViewProps) {
  const { t } = useTranslation(NS.Shared);

  if (organization) {
    const { logo, name } = organization;
    return (
      <div className="min-h-screen bg-brand-blue pt-3 text-center">
        <div className="flex items-center pl-3 text-left">
          <img className="max-w-[130px]" src={logo} alt={`${name}'s logo`} />
          <span className="border-left ml-3 border-solid border-white pl-3 text-xl font-bold text-white">
            {t('eligibilityResult.eligibility-check')}
          </span>
        </div>
        <div className="mx-10 mt-12 text-lg font-bold text-white lg:text-2xl">
          {t('eligibilityResult.sorry-ineligible')}
        </div>
      </div>
    );
  }
  return (
    <div className="block lg:flex">
      <SuperVisasSideBar />
      <SuperVisaIneligibleView submission={submission} />
    </div>
  );
}

interface EligibleProgramsViewProps {
  eligibleProgramIds: Set<number>;
  organization?: Organization;
  submission: EligibilitySubmission;
  user?: SimpleUserWithFullName | null; // null when the submission is not linked with any user and vice versa
}

function EligibleProgramsView({ eligibleProgramIds, organization, submission, user }: EligibleProgramsViewProps) {
  const { data: programTypes } = useProgramTypesQuery();
  const { data: userInfoData } = useUserInfoQuery({ detail: true });
  const loggedInUser = userInfoData?.user_info;
  const { submission_token: submissionId, program_type: programType } = submission;
  const selectedLng = getItemFromLocalStorage('i18nextLng') || 'en';
  const { t } = useTranslation([NS.Shared, NS.Eligibility]);
  const history = useHistory();
  const [isCreatingApplication, setIsCreatingApplication] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterTypes, setFilterTypes] = useState<Set<Type>>(new Set());

  const onProgramTypeClicked = (type: Type) => {
    const currentFilterTypes = new Set<Type>(filterTypes);
    if (currentFilterTypes.has(type)) {
      currentFilterTypes.delete(type);
    } else {
      currentFilterTypes.add(type);
    }

    setFilterTypes(currentFilterTypes);
  };

  // when user is logged in, create application for this user
  // otherwise, redirects to sign up page
  const onCreateApplication = async (
    program: Program,
    subApplicant?: { firstName: string; lastName: string; id: number | null },
    isAssignedToSelf?: boolean,
  ) => {
    if (loggedInUser) {
      setIsCreatingApplication(true);
      try {
        const res = await apiClient.createApplicationFromDefaultLeadGenPortal({
          userId: loggedInUser.id,
          programId: program.id,
          submissionId,
          subApplicant: subApplicant
            ? {
                id: subApplicant.id,
                first_name: subApplicant.firstName,
                last_name: subApplicant.lastName,
                user_manager: user?.id,
              }
            : null,
          isAssignedToSelf,
        });
        const application = await res.json();
        window.location.href = `https://apply.${window.location.host}/user/applications/${application.id}?lng=${selectedLng}`;
      } catch (err) {
        setIsCreatingApplication(false);
      }
    } else {
      history.push(`/eligibility/${submissionId}/sign_up?programId=${program.id}`);
    }
  };

  if (!userInfoData) {
    return null;
  }

  // when the submission is linked with a user, and the client is not logged in as this user
  // make the public result page unavailable
  if (user && user.id !== userInfoData.user_info?.id) {
    return (
      <div className="min-h-screen w-full bg-white pt-3 lg:w-3/4 lg:pr-24">
        <div className="px-8">
          <div className="mb-6 text-lg font-semibold text-gray-600 lg:text-lg">
            {t('eligibilityResult.access-denies')}
          </div>
          <div className="mb-24 w-full rounded border-2 border-solid border-gray-200 p-3 font-medium text-gray-700">
            {t('eligibilityResult.access-denies-message')}
          </div>
        </div>
        <LeadActions isEligible={false} submission={submission} />
      </div>
    );
  }
  // at this point, there are two cases:
  // 1. the submission is not linked with any user
  // 2. the submission is linked with the currently logged in user
  return (
    <>
      <Spinner show={isCreatingApplication} />
      <div className="min-h-screen w-full !bg-slate-50 lg:w-3/4 lg:pb-6">
        <div className="flex flex-col pb-[30px] pt-3">
          <div className="prose mx-5">
            <h2>{t('program.eligible-programs')}</h2>
            <p>
              <Trans i18nKey="eligibilityResult.eligible-programs" t={t}>
                Based on your answers, we found {{ number: eligibleProgramIds.size }}{' '}
                <span className="font-semibold text-brand-blue">
                  {{ programType: t(`${NS.Eligibility}:typeSelection.${submission.program_type}`) }}
                </span>
                {{ visas: t('program.program', { count: eligibleProgramIds.size }) }}
                you may be eligible for
              </Trans>
            </p>
            {programType === 'visitor' && (
              <div className="mb-6 text-base font-medium text-gray-600 lg:text-lg">{t('program.visitor-guide')}</div>
            )}
          </div>
        </div>
        {organization && (
          <ProgramTypesFilter
            className="mb-8 text-center"
            isOpen={isDropdownOpen}
            toggle={() => {
              setIsDropdownOpen(!isDropdownOpen);
            }}
            filterTypes={filterTypes}
            onItemClick={onProgramTypeClicked}
            programTypes={programTypes}
          />
        )}
        <div className="block space-y-8 px-5 lg:flex lg:flex-wrap lg:justify-start lg:gap-4 lg:space-y-0">
          {Array.from(eligibleProgramIds).map((id) => (
            <DynamicProgramCard
              key={id}
              filterTypes={filterTypes}
              programId={id}
              applications={(userInfoData.applications || []) as SimpleApplication[]}
              subApplicants={userInfoData.sub_applicants || []}
              user={loggedInUser as User}
              createApplication={typeof user !== 'undefined' ? onCreateApplication : undefined}
            />
          ))}
        </div>
        {!organization && (
          <LeadActions isEligible submission={submission} hideScheduleACall={programType === 'visitor'} />
        )}
      </div>
    </>
  );
}

interface EligibleProgramsResultProps {
  submission: EligibilitySubmission;
  organization?: Organization;
  user?: SimpleUserWithFullName | null;
}

function EligibleProgramsResult({ submission, organization, user }: EligibleProgramsResultProps) {
  const eligibleProgramIds = new Set(submission.result?.eligible_programs || []);
  const hasEligiblePrograms = eligibleProgramIds.size > 0;

  useEffect(() => {
    window.gtag?.('event', hasEligiblePrograms ? 'result_eligible' : 'result_ineligible', {
      event_label: 'eligibility',
      event_category: 'engagement',
      non_interaction: true,
    });
  }, [hasEligiblePrograms, submission]);

  const isVisaExempt = submission.result?.note?.visitor_visa_exempted || false;
  const isETAEligible = submission.result?.note?.eta_eligible || false;
  if (isVisaExempt || isETAEligible) {
    return (
      <div className="block lg:flex">
        <CrsSection organization={organization} crsScore={submission.crs_score?.total || 0} />
        <div className="min-h-screen w-full bg-gray-100 p-10">
          <VisitorVisaExemptView country={submission.country} needETA={isETAEligible} usage="external" />
        </div>
      </div>
    );
  }

  if (!hasEligiblePrograms) {
    return <IneligibleProgramsView organization={organization} submission={submission} />;
  }

  return (
    <div className="block lg:flex">
      <CrsSection organization={organization} crsScore={submission.crs_score?.total || 0} />
      <EligibleProgramsView
        eligibleProgramIds={eligibleProgramIds}
        organization={organization}
        submission={submission}
        user={user}
      />
    </div>
  );
}

export default EligibleProgramsResult;
